import { Component, signal } from "@angular/core";
import {
  ActivatedRoute,
  NavigationEnd,
  Router,
  RouterLink,
  RouterLinkActive,
  RouterOutlet,
} from "@angular/router";
import { CommonModule } from "@angular/common";
import { ImportsModule } from "../imports";
import { Loader } from "../helper/loader/loader";
import { LoadingService } from "../services/ShareService";
import { User } from "../models/Common/User";
import { UserService } from "../services/UserService";
import { Observable } from "rxjs";
import { EmailConfig, SMTPConfig } from "./account/email-config/email-config";

// ── Warning model ───────────────────────────────────────
export interface AppWarning {
  Id: string;
  Title: string;
  Message?: string;
  Date?: string | Date;
  Severity: "1" | "2" | "3";
  ActionType?: string; // optional CTA label
  ActionUrl?: string; // optional route to navigate on action click
  EmailServer?: any;
}

@Component({
  selector: "app-root",
  imports: [
    RouterOutlet,
    RouterLink,
    RouterLinkActive,
    CommonModule,
    ImportsModule,
    Loader,
    EmailConfig,
  ],
  templateUrl: "./app.html",
  styleUrl: "./app.scss",
  providers: [UserService],
})
export class App {
  protected readonly title = signal("dot-leads-updated");
  emailConfigVisible: boolean = false;
  is_top_menu_visible = false;
  isDarkMode = false;
  loading = false;

  config: any = {};

  // ── Warnings ──────────────────────────────────────────
  // `warnings` is a signal (not a plain array) because this app runs with
  // zoneless change detection: it's populated asynchronously from an HTTP
  // response, and a plain-property mutation there can land in the gap
  // between a render pass and Angular's verification pass — especially
  // under heavy scheduler activity like ag-Grid's scroll handling — which
  // is what was producing the NG0100 error. Signal writes are scheduled
  // deterministically and don't have that race.
  warnings = signal<AppWarning[]>([]);
  warningsDrawerOpen = false;
  warningsLoading = false;

  user: User = JSON.parse(localStorage.getItem("mcl_current_user")!);
  showMigrationDialog: boolean = false;
  showNotificationDialog: boolean = false;
  notificationWarning: AppWarning | null = null;

  openEmailSettings() {
    this.showMigrationDialog = false;
    this.emailConfigVisible = true;
  }
  constructor(
    private route: Router,
    private loadingService: LoadingService,
    private userService: UserService,
  ) {
    this.loadingService.loading$.subscribe((value) => {
      this.loading = value;
    });
  }

  ngOnInit() {
    this.route.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        const url = event.url;

        if (url === "/") {
          this.is_top_menu_visible = false;
          this.isDarkMode = false;
          return;
        }

        if (
          url.includes("/login") ||
          url.includes("print") ||
          url.includes("map")
        ) {
          this.is_top_menu_visible = false;
          this.isDarkMode = false;
          return;
        }

        if (url.includes("search")) {
          setTimeout(() => {
            this.user = JSON.parse(localStorage.getItem("mcl_current_user")!);
          });
        }

        this.is_top_menu_visible = true;

        // Load warnings once after the user is logged in

        if (this.user) {
          //this.showMigrationDialog = true;
        }

        if (this.user && this.warnings().length === 0) {
          this.loadWarnings();
        }
      }
    });
  }

  // ── Load warnings from API ────────────────────────────
  private loadWarnings(): void {
    this.warningsLoading = true;
    (
      this.userService.get_notifications() as Observable<AppWarning[]>
    ).subscribe({
      next: (res: AppWarning[]) => {
        this.warningsLoading = false;
        this.warnings.set(res || []);

        const notification = (res || []).find((el) => el.ActionType == "2");
        if (notification) {
          this.notificationWarning = notification;
          this.showNotificationDialog = true;
        }
      },
      error: () => {
        this.warningsLoading = false;
      },
    });
  }

  // ── Notification dialog close ─────────────────────────
  closeNotificationDialog(): void {
    this.showNotificationDialog = false;
    this.userService
      .make_read_notification(this.notificationWarning!.Id)
      .subscribe(() => {
        this.notificationWarning = null;
      });
  }

  // ── Drawer open / close ───────────────────────────────
  openWarningsDrawer(): void {
    this.warningsDrawerOpen = true;
    document.body.style.overflow = "hidden";
  }

  closeWarningsDrawer(): void {
    this.warningsDrawerOpen = false;
    document.body.style.overflow = "";
  }

  // ── Warning action button handler ─────────────────────
  handleWarningAction(warning: AppWarning): void {
    this.closeWarningsDrawer();

    if (warning.ActionType == "0" || warning.ActionType == "1") {
      this.emailConfigVisible = true;
      this.config = warning.EmailServer;
    }

    // if (warning.ActionUrl) {
    //   this.route.navigate([warning.ActionUrl]);
    // }
  }

  // ── Auth ──────────────────────────────────────────────
  logout(): void {
    localStorage.clear();
    window.open("/home/logout", "_self");
  }

  // openUserProfile(): void {
  //   this.route.navigate(["/account/profile"]);
  // }
}
